import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import ScheduleItemModal from '../../components/Schedule/ScheduleItemModal';
import ScheduleItemFolioModal from '../../components/Schedule/ScheduleItemFolioModal';
import ManageSchedulesModal from '../../components/Schedule/ManageSchedulesModal';
import ConfirmModal from '../../components/utils/ConfirmModal';
import { sortRoomsForDesignFlow } from '../../utils/designWorkflow';
import '../../component-styles/RecordPages.css';
import '../../component-styles/ScheduleSection.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import logoImg from '../../assets/default-if-logo.png';
import { BUILT_IN_SCHEDULE_STATUSES, withCustomStatuses } from '../../utils/orderStatuses.js';

const PDF_HIDEABLE_COLUMNS = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'measurement', label: 'Measurement' },
  { key: 'description', label: 'Description' },
  { key: 'leadTime', label: 'Lead Time' },
  { key: 'status', label: 'Status' },
  { key: 'cfa', label: 'CFA' },
];

const TYPE_OPTIONS = [
  { value: 'ffe', label: 'FF&E' },
  { value: 'finish', label: 'Finishes' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'millwork', label: 'Millwork' },
  { value: 'appliance', label: 'Appliances' },
  { value: 'other', label: 'Other' },
];

function loadLogoForPDF(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('timeout')), 6000);
    img.onload = () => {
      clearTimeout(timer);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      try {
        resolve({ data: canvas.toDataURL('image/png'), aspect: img.naturalWidth / img.naturalHeight });
      } catch {
        reject(new Error('canvas tainted'));
      }
    };
    img.onerror = () => { clearTimeout(timer); reject(new Error('load error')); };
    img.src = url;
  });
}

export default function ScheduleSection() {
  const { projectId } = useParams();
  const userId = localStorage.getItem('userId');
  const outlet = useOutletContext?.() || {};
  const projectName = outlet.project?.projectNickname || outlet.project?.name || 'Project Schedule';

  const { permissions } = usePermissions();
  const canEdit = permissions.createEditInvoicesProposals !== false;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState('');
  const [importing, setImporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [hiddenPdfColumns, setHiddenPdfColumns] = useState(() => new Set());
  const [rooms, setRooms] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [managingSchedules, setManagingSchedules] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [customOrderStatuses, setCustomOrderStatuses] = useState([]);
  const [folioItem, setFolioItem] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!userId || !projectId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        { cache: 'no-store' }
      );
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch schedule items:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!userId || !projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setPurchaseOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch purchase orders:', err);
      }
    })();
  }, [userId, projectId]);

  // Custom statuses are account-level and shared with purchase orders, so an
  // item imported from a PO keeps the status it was given there.
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const settings = await res.json();
        setCustomOrderStatuses(
          Array.isArray(settings?.customOrderStatuses) ? settings.customOrderStatuses : []
        );
      } catch {
        // non-fatal — falls back to the built-in statuses
      }
    })();
  }, [userId]);

  useEffect(() => {
    if (!userId || !projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/library/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}?sectionsOnly=true`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setRooms(
          sortRoomsForDesignFlow(
            (data.sections || [])
              .filter((s) => s.type === 'section')
              .map((s) => ({ ...s, sectionId: s.sectionId || s.sk }))
          )
        );
      } catch (err) {
        console.error('Failed to fetch rooms:', err);
      }
    })();
  }, [userId, projectId]);

  const fetchSchedules = useCallback(async () => {
    if (!userId || !projectId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        { cache: 'no-store' }
      );
      const data = await res.json();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  }, [userId, projectId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  function handleScheduleCreated(newSchedule) {
    setSchedules((prev) => [...prev, newSchedule].sort((a, b) => a.name.localeCompare(b.name)));
  }

  const scheduleGroups = useMemo(() => {
    const nameById = new Map(schedules.map((s) => [s.sk, s.name]));
    const groups = {};
    for (const item of items) {
      const label = (item.scheduleId && nameById.get(item.scheduleId)) || 'Unassigned';
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [items, schedules]);

  async function updateItemField(sk, field, value) {
    setItems((prev) => prev.map((i) => (i.sk === sk ? { ...i, [field]: value } : i)));
    try {
      await fetch(`${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(sk)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) {
      console.error('Failed to update schedule item:', err);
    }
  }

  async function deleteItem(sk) {
    setItems((prev) => prev.filter((i) => i.sk !== sk));
    try {
      await fetch(`${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(sk)}`, {
        method: 'DELETE',
      });
    } catch (err) {
      console.error('Failed to delete schedule item:', err);
    }
  }

  function handleItemSaved(saved) {
    setItems((prev) => {
      const exists = prev.some((i) => i.sk === saved.sk);
      return exists ? prev.map((i) => (i.sk === saved.sk ? saved : i)) : [...prev, saved];
    });
    setModalState(null);
  }

  async function handleImportFromPo() {
    if (!selectedPoId) return;
    const po = purchaseOrders.find((p) => p.poId === selectedPoId);
    const poLabel = po?.orderNumber || 'the purchase order';
    setImporting(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/import-from-po/${encodeURIComponent(selectedPoId)}`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to import purchase order items.');

      const imported = Number(data?.imported) || 0;
      const skipped = Number(data?.skipped) || 0;
      const plural = (n) => `${n} item${n === 1 ? '' : 's'}`;
      if (imported && skipped) {
        notify.success(
          `Imported ${plural(imported)} from ${poLabel}. ${plural(skipped)} already on the schedule ${skipped === 1 ? 'was' : 'were'} skipped.`
        );
      } else if (imported) {
        notify.success(`Imported ${plural(imported)} from ${poLabel}.`);
      } else if (skipped) {
        notify.info(`Everything on ${poLabel} is already on the schedule.`);
      } else {
        notify.info(`${poLabel} has no items to import.`);
      }

      setSelectedPoId('');
      await fetchItems();
    } catch (err) {
      console.error('Failed to import from PO:', err);
      notify.error(err.message || 'Failed to import purchase order items.');
    } finally {
      setImporting(false);
    }
  }

  async function handleDownloadPdf() {
    setPdfExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      let logo = null;
      try { logo = await loadLogoForPDF(logoImg); } catch {}

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.width;
      const pageH = doc.internal.pageSize.height;
      const ML = 14;
      const MR = 14;
      const tableW = pageW - ML - MR;

      const C_CHARCOAL = [17, 24, 39];
      const C_HEADER_BG = [98, 121, 78];
      const C_TEAL = [165, 210, 125];
      const C_WHITE = [255, 255, 255];
      const C_OFF_WHITE = [247, 247, 245];
      const C_BORDER = [229, 231, 235];
      const C_MUTED = [107, 114, 128];
      const C_TEXT = [17, 24, 39];

      const HEADER_BOT = 30;
      if (logo) {
        const LOGO_H = 12;
        const LOGO_W = LOGO_H * logo.aspect;
        doc.addImage(logo.data, 'PNG', pageW - MR - LOGO_W, 7, LOGO_W, LOGO_H);
      }

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...C_CHARCOAL);
      doc.text('Project Schedule', ML, 15);

      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...C_MUTED);
      doc.text(projectName, ML, 23);
      doc.text(
        `Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
        pageW - MR, 23, { align: 'right' }
      );

      doc.setDrawColor(...C_TEAL);
      doc.setLineWidth(0.6);
      doc.line(ML, HEADER_BOT, pageW - MR, HEADER_BOT);

      const allColumns = [
        { key: 'room', header: 'Room', dataKey: 'room', cellWidth: tableW * 0.12, required: true },
        { key: 'name', header: 'Item', dataKey: 'name', cellWidth: tableW * 0.18, required: true },
        { key: 'qty', header: 'Qty / Unit', dataKey: 'qty', cellWidth: tableW * 0.09, required: true },
        { key: 'vendor', header: 'Vendor', dataKey: 'vendor', cellWidth: tableW * 0.13 },
        { key: 'measurement', header: 'Measurement', dataKey: 'measurement', cellWidth: tableW * 0.12 },
        { key: 'description', header: 'Description', dataKey: 'description', cellWidth: tableW * 0.16 },
        { key: 'leadTime', header: 'Lead Time', dataKey: 'leadTime', cellWidth: tableW * 0.09 },
        { key: 'status', header: 'Status', dataKey: 'status', cellWidth: tableW * 0.11 },
        { key: 'cfa', header: 'CFA', dataKey: 'cfa', cellWidth: tableW * 0.07 },
      ];
      const columns = allColumns.filter((c) => c.required || !hiddenPdfColumns.has(c.key));

      let cursorY = HEADER_BOT + 8;

      scheduleGroups.forEach(([label, groupItems]) => {
        if (cursorY > pageH - 40) {
          doc.addPage();
          cursorY = 20;
        }
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C_CHARCOAL);
        doc.text(label, ML, cursorY);
        cursorY += 4;

        const bodyRows = groupItems.map((item) => ({
          room: item.room || 'Unassigned',
          name: item.name || '',
          qty: `${item.qty || 1} ${item.unitType || 'Each'}`,
          vendor: item.vendor || '-',
          measurement: item.measurement || '-',
          description: item.description || '-',
          leadTime: item.leadTime || '-',
          status: item.status || 'Draft',
          cfa: item.cfaApproved ? 'Yes' : 'No',
        }));

        autoTable(doc, {
          startY: cursorY,
          columns,
          body: bodyRows,
          margin: { left: ML, right: MR, bottom: 16 },
          styles: {
            font: 'helvetica',
            fontSize: 8.5,
            cellPadding: 2.5,
            overflow: 'ellipsis',
            valign: 'middle',
            textColor: C_TEXT,
            lineColor: C_BORDER,
            lineWidth: 0.15,
          },
          headStyles: {
            fillColor: C_HEADER_BG,
            textColor: C_WHITE,
            fontStyle: 'bold',
            fontSize: 8,
          },
          bodyStyles: { fillColor: C_WHITE },
          alternateRowStyles: { fillColor: C_OFF_WHITE },
        });

        cursorY = doc.lastAutoTable.finalY + 10;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...C_BORDER);
        doc.setLineWidth(0.2);
        doc.line(ML, pageH - 12, pageW - MR, pageH - 12);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C_MUTED);
        doc.text('InteriorFolio — Confidential', ML, pageH - 7);
        doc.text(`Page ${i} of ${pageCount}`, pageW - MR, pageH - 7, { align: 'right' });
      }

      const slug = projectName.replace(/\s+/g, '-');
      doc.save(`schedule-${slug}.pdf`);
    } finally {
      setPdfExporting(false);
    }
  }

  return (
    <section className="record-section">
      <div className="record-top-row">
        <div className="record-header-bar">
          <h2 className="record-title">Schedule</h2>
        </div>
        <p className="record-subtext">
          Items your client has approved, organized by schedule for procurement and installation tracking.
        </p>
      </div>

      <div className="schedule-controls-row">
        {canEdit && (
          <div className="schedule-import-control">
            <select
              className="if-input"
              value={selectedPoId}
              onChange={(e) => setSelectedPoId(e.target.value)}
            >
              <option value="">Import from Purchase Order…</option>
              {/* The saved status shows here because it's what the imported
                  items start with — an edit still unsaved on the PO isn't. */}
              {purchaseOrders.map((po) => (
                <option key={po.poId} value={po.poId}>
                  {po.orderNumber || po.poId} — {po.vendorName || 'No vendor'} ·{' '}
                  {po.status || 'No status'}
                </option>
              ))}
            </select>
            <button
              className="record-add-btn"
              disabled={!selectedPoId || importing}
              onClick={handleImportFromPo}
            >
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
        )}

        <div className="schedule-controls-row-right">
          {canEdit && (
            <button className="record-add-btn" onClick={() => setManagingSchedules(true)}>
              Manage Schedules
            </button>
          )}
          {canEdit && (
            <button className="record-add-btn" onClick={() => setModalState({ mode: 'add' })}>
              + Add Item
            </button>
          )}
          <div className="schedule-pdf-menu-wrap">
            <button
              className="record-add-btn"
              disabled={pdfExporting || items.length === 0}
              onClick={() => setPdfMenuOpen((o) => !o)}
            >
              {pdfExporting ? 'Generating…' : 'Download PDF'}
            </button>
            {pdfMenuOpen && (
              <div className="schedule-pdf-menu">
                <div className="schedule-pdf-menu-heading">Hide</div>
                {PDF_HIDEABLE_COLUMNS.map((col) => (
                  <label key={col.key} className="schedule-pdf-menu-option">
                    <input
                      type="checkbox"
                      checked={hiddenPdfColumns.has(col.key)}
                      onChange={() =>
                        setHiddenPdfColumns((prev) => {
                          const next = new Set(prev);
                          if (next.has(col.key)) next.delete(col.key);
                          else next.add(col.key);
                          return next;
                        })
                      }
                    />
                    {col.label}
                  </label>
                ))}
                <button
                  className="record-add-btn schedule-pdf-menu-download"
                  disabled={pdfExporting}
                  onClick={async () => {
                    await handleDownloadPdf();
                    setPdfMenuOpen(false);
                  }}
                >
                  {pdfExporting ? 'Generating…' : 'Download PDF'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="record-card">
        {loading ? (
          <div className="record-empty">Loading schedule…</div>
        ) : items.length === 0 ? (
          <div className="record-empty">
            No items yet. Add an item manually, add from an invoice, or import from a purchase order above.
          </div>
        ) : (
          <div className="record-table-wrap schedule-table-wrap">
            <table className="record-table schedule-table">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Item</th>
                  <th>Qty / Unit</th>
                  <th>Vendor</th>
                  <th>Measurement</th>
                  <th>Description</th>
                  <th>Lead Time</th>
                  <th>Status</th>
                  <th>CFA</th>
                  <th>Folio</th>
                </tr>
              </thead>
              <tbody>
                {scheduleGroups.map(([label, groupItems]) => (
                  <React.Fragment key={label}>
                    <tr className="schedule-category-row">
                      <td colSpan={10}>{label}</td>
                    </tr>
                    {groupItems.map((item) => (
                      <tr key={item.sk}>
                        <td>
                          <select
                            className="if-input"
                            value={item.room || ''}
                            disabled={!canEdit}
                            onChange={(e) => updateItemField(item.sk, 'room', e.target.value)}
                          >
                            <option value="">Unassigned</option>
                            {rooms.map((r) => (
                              <option key={r.sectionId || r.name} value={r.name}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="schedule-item-cell">
                            {item.imageUrl && (
                              <img className="schedule-item-thumb" src={item.imageUrl} alt="" />
                            )}
                            <span className="schedule-item-name" title={item.name}>
                              {item.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          {item.qty || 1} {item.unitType || 'Each'}
                        </td>
                        <td>{item.vendor || '—'}</td>
                        <td>{item.measurement || '—'}</td>
                        <td className="desc-cell">{item.description || '—'}</td>
                        <td>{item.leadTime || '—'}</td>
                        <td>
                          <select
                            className="if-input"
                            value={item.status || 'Draft'}
                            disabled={!canEdit}
                            onChange={(e) => updateItemField(item.sk, 'status', e.target.value)}
                          >
                            {withCustomStatuses(
                              BUILT_IN_SCHEDULE_STATUSES,
                              customOrderStatuses,
                              item.status
                            ).map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="schedule-cfa-cell">
                          <input
                            type="checkbox"
                            checked={!!item.cfaApproved}
                            disabled={!canEdit}
                            onChange={(e) => updateItemField(item.sk, 'cfaApproved', e.target.checked)}
                            aria-label={`CFA approved for ${item.name || 'schedule item'}`}
                          />
                        </td>
                        <td>
                          <div className="schedule-row-actions">
                            <button
                              className="folio-icon-btn"
                              type="button"
                              onClick={() => setFolioItem(item)}
                              aria-label={`Open folio for ${item.name || 'schedule item'}`}
                            >
                              <img
                                src="/folder-icon.png"
                                alt="Open folio"
                                className="link-icon"
                                width={18}
                                height={18}
                                draggable={false}
                              />
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => setDeleteConfirmItem(item)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalState && (
        <ScheduleItemModal
          item={modalState.mode === 'edit' ? modalState.item : null}
          typeOptions={TYPE_OPTIONS}
          statusOptions={withCustomStatuses(BUILT_IN_SCHEDULE_STATUSES, customOrderStatuses)}
          rooms={rooms}
          schedules={schedules}
          onScheduleCreated={handleScheduleCreated}
          userId={userId}
          projectId={projectId}
          onClose={() => setModalState(null)}
          onSaved={handleItemSaved}
        />
      )}

      {folioItem && (
        <ScheduleItemFolioModal
          item={folioItem}
          schedules={schedules}
          projectId={projectId}
          onClose={() => setFolioItem(null)}
          onEdit={canEdit ? (item) => setModalState({ mode: 'edit', item }) : null}
          onDelete={canEdit ? deleteItem : null}
        />
      )}

      {managingSchedules && (
        <ManageSchedulesModal
          userId={userId}
          projectId={projectId}
          schedules={schedules}
          onChange={setSchedules}
          onScheduleDeleted={fetchItems}
          onClose={() => setManagingSchedules(false)}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(deleteConfirmItem)}
        title="Delete Schedule Item"
        message={`Delete "${deleteConfirmItem?.name || 'this item'}" from the schedule? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          deleteItem(deleteConfirmItem.sk);
          setDeleteConfirmItem(null);
        }}
        onCancel={() => setDeleteConfirmItem(null)}
      />
    </section>
  );
}
